import type { Editor } from "@tiptap/core";
import { Link } from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import DOMPurify from "dompurify";
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "span",
];
const ALLOWED_ATTR = ["href", "target", "rel", "class"];

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

export type RichTextEditorVariant = "primary" | "secondary";

export interface RichTextEditorProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  variant?: RichTextEditorVariant;
  fullWidth?: boolean;
  readOnly?: boolean;
  /** Minimum height of the editor area (e.g. "280px"). Default 280px. */
  minHeight?: string;
}

const variantStyles = {
  primary: {
    label:
      "block text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-2",
    wrapper:
      "border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 focus-within:border-primary-400 transition-colors duration-300",
    error: "mt-1 text-sm text-red-600 dark:text-red-400",
  },
  secondary: {
    label: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2",
    wrapper:
      "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-blue-500 transition duration-150",
    error: "mt-1 text-sm text-red-600 dark:text-red-400",
  },
};

const emptyHtml = "<p></p>";

function isEmpty(html: string): boolean {
  const s = html.trim();
  return s === "" || s === "<p></p>" || s === "<p><br></p>";
}

interface ToolbarProps {
  editor: Editor | null;
  readOnly: boolean;
}

function Toolbar({ editor, readOnly }: ToolbarProps) {
  if (!editor || readOnly) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("URL", previous ?? "https://");
    if (url != null) {
      if (url === "") editor.chain().focus().extendMarkRange("link").unsetLink().run();
      else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  return (
    <div className="rich-text-editor-tiptap-toolbar flex flex-wrap items-center gap-1 border-b border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900 p-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive("heading", { level: 1 }) ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}
        title="Heading 1"
      >
        H1
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive("heading", { level: 2 }) ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}
        title="Heading 2"
      >
        H2
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive("heading", { level: 3 }) ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}
        title="Heading 3"
      >
        H3
      </Button>
      <Separator orientation="vertical" className="h-4 mx-0.5" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive("bold") ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}
        title="Bold"
      >
        <strong>B</strong>
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`italic ${editor.isActive("italic") ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}`}
        title="Italic"
      >
        I
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`underline ${editor.isActive("underline") ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}`}
        title="Underline"
      >
        U
      </Button>
      <Separator orientation="vertical" className="h-4 mx-0.5" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive("orderedList") ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}
        title="Ordered list"
      >
        OL
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive("bulletList") ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}
        title="Bullet list"
      >
        UL
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={setLink}
        className={editor.isActive("link") ? "bg-primary-200 dark:bg-primary-700 text-primary-950 dark:text-white" : "text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"}
        title="Link"
      >
        Link
      </Button>
      <Separator orientation="vertical" className="h-4 mx-0.5" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        className="text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800"
        title="Clear formatting"
      >
        Clear
      </Button>
    </div>
  );
}

const RichTextEditorInner = forwardRef<Editor | null, RichTextEditorProps>(
  (
    {
      id,
      label,
      value,
      onChange,
      placeholder,
      error,
      variant = "primary",
      fullWidth = true,
      readOnly = false,
      minHeight = "280px",
    },
    ref
  ) => {
    const styles = variantStyles[variant];
    const editorId = id ?? `rich-text-${Math.random().toString(36).slice(2, 11)}`;
    const onChangeRef = useRef(onChange);
    const valueRef = useRef(value);

    useLayoutEffect(() => {
      onChangeRef.current = onChange;
      valueRef.current = value;
    });

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
        }),
        Placeholder.configure({
          placeholder: placeholder ?? "",
        }),
      ],
      content: sanitizeHtml(value || "") || emptyHtml,
      editable: !readOnly,
      editorProps: {
        attributes: {
          "data-placeholder": placeholder ?? "",
        },
      },
      onUpdate: ({ editor: e }) => {
        const html = e.getHTML();
        if (html !== valueRef.current) {
          valueRef.current = html;
          onChangeRef.current?.(html);
        }
      },
    });

    useImperativeHandle(ref, () => editor, [editor]);

    // Sync controlled value when it changes from outside
    useEffect(() => {
      if (!editor) return;
      const current = editor.getHTML();
      const normalized = value ?? "";
      const sanitized = sanitizeHtml(normalized) || emptyHtml;
      const currentEmpty = isEmpty(current);
      const valueEmpty = isEmpty(sanitized);
      if (currentEmpty && valueEmpty) return;
      if (current === sanitized) return;
      editor.commands.setContent(sanitized, { emitUpdate: false });
      valueRef.current = sanitized;
    }, [editor, value]);

    useEffect(() => {
      if (editor) editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    return (
      <div className={fullWidth ? "w-full" : ""}>
        {label && (
          <label htmlFor={editorId} className={styles.label}>
            {label}
          </label>
        )}
        <div
          id={editorId}
          className={`rich-text-editor-tiptap rounded-none ${styles.wrapper}`}
        >
          <Toolbar editor={editor} readOnly={readOnly} />
          <div
            className="rich-text-editor-tiptap-content font-light text-primary-950 dark:text-white overflow-auto"
            style={{ minHeight }}
          >
            <EditorContent editor={editor} />
          </div>
        </div>
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }
);

RichTextEditorInner.displayName = "RichTextEditor";

export const RichTextEditor = RichTextEditorInner;
